import { useMemo } from "react";
import { makeUrl, routes } from "../../lib/urls";
import { DonutChart } from "../DonutChart";
import type { StatusSegment } from "../StatusBreakdownChart";
import type { AiProviderAllocationRow } from "./aiProviderMonitoring";
import { useMonitoringInteraction } from "./analytics";
import { formatEstimatedCostUsd } from "./aiUsage";
import { splitBySharePrefix } from "./foldSegments";
import { OTHER_SPACES_KEY, SPACE_FOLD } from "./spaceSegments";

const AVAILABLE_KEY = "__available__";

type Props = {
  rows: readonly AiProviderAllocationRow[];
  colorMap: ReadonlyMap<string, string>;
  trackColorMap: ReadonlyMap<string, string>;
};

export function AiProviderSpendDonut({ rows, colorMap, trackColorMap }: Props) {
  const recordInteraction = useMonitoringInteraction();
  const sliceRows = useMemo(
    () => rows.filter((row) => row.kind !== "total"),
    [rows]
  );
  const spaceIds = useMemo(
    () =>
      new Set(rows.filter((row) => row.kind === "space").map((row) => row.id)),
    [rows]
  );
  const totalSpend = useMemo(
    () => sliceRows.reduce((sum, row) => sum + (row.spendUsd ?? 0), 0),
    [sliceRows]
  );
  const totalCapacity = useMemo(
    () =>
      sliceRows.reduce(
        (sum, row) => sum + (row.limitAmount ?? row.spendUsd ?? 0),
        0
      ),
    [sliceRows]
  );
  const providerCap =
    rows.find((row) => row.kind === "total")?.limitAmount ?? null;
  // Size the ring to the provider cap so spend fills it only as the cap is approached.
  const donutTotal = Math.max(providerCap ?? 0, totalCapacity);
  const segments = useMemo<StatusSegment[]>(() => {
    const cap = (row: AiProviderAllocationRow) =>
      row.limitAmount ?? row.spendUsd ?? 0;
    const toSegment = (row: AiProviderAllocationRow): StatusSegment => ({
      key: row.id,
      label: row.label,
      count: row.spendUsd ?? 0,
      capacity: cap(row),
      color:
        row.kind === "personal"
          ? "var(--gray-600)"
          : row.kind === "unattributed"
            ? "var(--gray-400)"
            : (colorMap.get(row.id) ?? "var(--alpha8)"),
      trackColor: trackColorMap.get(row.id) ?? "var(--alpha4)",
      caption:
        row.limitAmount != null
          ? `of ${formatEstimatedCostUsd(row.limitAmount)}`
          : "No limit",
    });

    // Fold the long tail of small per-space arcs into one "Other"; personal and
    // unattributed keep their own buckets.
    const spaceRows = sliceRows
      .filter((row) => row.kind === "space")
      .sort((a, b) => cap(b) - cap(a));
    const { kept, folded } = splitBySharePrefix(
      spaceRows,
      cap,
      donutTotal,
      SPACE_FOLD
    );

    const base: StatusSegment[] = kept.map(toSegment);
    if (folded.length > 0) {
      const foldedCapacity = folded.reduce((sum, row) => sum + cap(row), 0);
      base.push({
        key: OTHER_SPACES_KEY,
        label: `${folded.length} more spaces`,
        count: folded.reduce((sum, row) => sum + (row.spendUsd ?? 0), 0),
        capacity: foldedCapacity,
        color: "var(--gray-600)",
        trackColor: "var(--alpha4)",
        caption: `of ${formatEstimatedCostUsd(foldedCapacity)}`,
        details: folded.map((row) => ({
          label: row.label,
          value: formatEstimatedCostUsd(row.spendUsd),
        })),
      });
    }
    base.push(
      ...sliceRows.filter((row) => row.kind !== "space").map(toSegment)
    );

    const remaining = donutTotal - totalCapacity;
    if (remaining > 0) {
      // Leftover cap not assigned to any space, shown as a hoverable slice; count === capacity keeps it solid.
      base.push({
        key: AVAILABLE_KEY,
        label: "Available",
        count: remaining,
        capacity: remaining,
        color: "var(--alpha4)",
        caption: `of ${formatEstimatedCostUsd(providerCap!)}`,
        hideFromLegend: true,
      });
    }
    return base;
  }, [
    sliceRows,
    colorMap,
    trackColorMap,
    donutTotal,
    totalCapacity,
    providerCap,
  ]);

  return (
    <DonutChart
      segments={segments}
      total={totalSpend}
      capacityTotal={donutTotal}
      formatValue={formatEstimatedCostUsd}
      centerLabel="All spend"
      centerCaption={
        providerCap != null
          ? `of ${formatEstimatedCostUsd(providerCap)}`
          : "spent"
      }
      strokeWidth={8}
      scrollableLegend
      placeholder={rows.length === 0}
      segmentHref={(s) =>
        spaceIds.has(s.key)
          ? makeUrl(routes.space, { spaceId: s.key })
          : undefined
      }
      onSegmentNavigate={(s) =>
        recordInteraction("ai.spend_vs_limits_open", {
          targetType: "space",
          targetId: s.key,
        })
      }
    />
  );
}
